// pages/Profile.js
import React, { useState, useEffect } from "react";
import { auth, db, app } from "../firebase";
import { doc, getDoc, collection, addDoc, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Container,
  Box,
  TextField,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#1a1a1a",
  padding: theme.spacing(2),
}));

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: 400,
  backgroundColor: "#2d2d2d",
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.5)",
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
  },
}));

const PostCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: "#262626",
  borderRadius: "10px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
}));

// Publish Movie Form Component (unchanged)
const PublishMovieForm = ({ onSubmit, onCancel }) => {
  const [uploading, setUploading] = useState(false);
  const [imageURL, setImageURL] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    genre: "",
    targetFunding: "",
    cast: "",
    crew: "",
    director: "",
    duration: "",
    unitPrice: "",
    publish: "0",
    posterURL: "",
  });

  async function handleImageChange(e) {
    const image = e.target.files[0];
    if (!image) return;

    try {
      setUploading(true);
      const storage = getStorage(app);
      const uniqueFileName = `posters/${Date.now()}_${image.name}`;
      const storageRef = ref(storage, uniqueFileName);

      await uploadBytes(storageRef, image);
      const downloadURL = await getDownloadURL(storageRef);

      setImageURL(downloadURL);
      setFormData((prev) => ({
        ...prev,
        posterURL: downloadURL,
      }));
    } catch (error) {
      console.error("Image Upload Error:", error.code, error.message);
      setSnackbarMessage("Failed to upload image. Please try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setUploading(false);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Typography variant="h6" sx={{ color: "#ffffff", mb: 2 }}>
          Publish a New Movie
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Title*"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          margin="normal"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#3d3d3d",
              "& fieldset": { borderColor: "#cccccc" },
              "& input": { color: "#ffffff" },
            },
          }}
        />
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Description*"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          multiline
          rows={3}
          margin="normal"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#3d3d3d",
              "& fieldset": { borderColor: "#cccccc" },
              "& textarea": { color: "#ffffff" },
            },
          }}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel sx={{ color: "#cccccc" }}>Genre</InputLabel>
          <Select
            name="genre"
            value={formData.genre}
            onChange={handleChange}
            required
            sx={{ backgroundColor: "#3d3d3d", color: "#ffffff" }}
          >
            {["Action", "Comedy", "Drama", "Sci-Fi", "Horror", "Romance", "Thriller", "Documentary"].map((genre) => (
              <MenuItem key={genre} value={genre}>{genre}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Target Funding (₹)*"
          name="targetFunding"
          type="number"
          value={formData.targetFunding}
          onChange={handleChange}
          required
          margin="normal"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#3d3d3d",
              "& input": { color: "#ffffff" },
            },
          }}
        />
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Cast (comma-separated)*"
          name="cast"
          value={formData.cast}
          onChange={handleChange}
          required
          margin="normal"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#3d3d3d",
              "& input": { color: "#ffffff" },
            },
          }}
        />
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Crew (comma-separated)*"
          name="crew"
          value={formData.crew}
          onChange={handleChange}
          required
          margin="normal"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#3d3d3d",
              "& input": { color: "#ffffff" },
            },
          }}
        />
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Director*"
          name="director"
          value={formData.director}
          onChange={handleChange}
          required
          margin="normal"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#3d3d3d",
              "& input": { color: "#ffffff" },
            },
          }}
        />
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Duration (e.g., 2h 30min)*"
          name="duration"
          value={formData.duration}
          onChange={handleChange}
          required
          margin="normal"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#3d3d3d",
              "& input": { color: "#ffffff" },
            },
          }}
        />
        <input type="file" accept="image/*" onChange={handleImageChange} style={{ marginTop: "10px" }} />
        {uploading && <Typography sx={{ color: "yellow" }}>Uploading...</Typography>}
        {imageURL && <img src={imageURL} alt="Movie Poster" width="100" height="100" style={{ marginTop: "10px" }} />}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Unit Price (₹)*"
          name="unitPrice"
          type="number"
          value={formData.unitPrice}
          onChange={handleChange}
          required
          margin="normal"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#3d3d3d",
              "& input": { color: "#ffffff" },
            },
          }}
        />
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            sx={{ backgroundColor: "#7d1aed", color: "#ffffff" }}
            fullWidth
          >
            Publish Movie
          </Button>
          <Button
            variant="outlined"
            sx={{ color: "#cccccc", borderColor: "#cccccc" }}
            fullWidth
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Box>
      </form>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%", backgroundColor: "#2d2d2d", color: "#ffffff" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

// Profile Component
const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPublishForm, setShowPublishForm] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [posts, setPosts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        console.log("User not authenticated, redirecting to login");
        navigate("/login");
        return;
      }

      const fetchUserData = async () => {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          } else {
            console.error("No such user document!");
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      };

      const fetchUserPosts = () => {
        console.log("Fetching posts for user:", user.uid);

        const postsQuery = query(
          collection(db, "posts"),
          where("userId", "==", user.uid),
          orderBy("timestamp", "desc")
        );

        const unsubscribe = onSnapshot(postsQuery, (snapshot) => {
          const postsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log("Fetched posts in Profile:", postsData);
          setPosts(postsData);
        }, (error) => {
        });

        return unsubscribe;
      };

      fetchUserData();
      const unsubscribePosts = fetchUserPosts();

      return () => {
        unsubscribePosts && unsubscribePosts();
      };
    });

    return () => unsubscribeAuth && unsubscribeAuth();
  }, [navigate]);

  const handlePublishMovie = async (formData) => {
    try {
      const movieData = {
        title: formData.title,
        description: formData.description,
        genre: formData.genre,
        targetFunding: parseFloat(formData.targetFunding),
        cast: formData.cast.split(",").map((item) => item.trim()),
        crew: formData.crew.split(",").map((item) => item.trim()),
        director: formData.director,
        duration: formData.duration,
        unitPrice: parseFloat(formData.unitPrice),
        producerId: auth.currentUser.uid,
        publish: formData.publish,
        posterURL: formData.posterURL,
        createdAt: new Date(),
      };

      await addDoc(collection(db, "movies"), movieData);

      setSnackbarMessage("Movie published successfully!");
      setSnackbarSeverity("success");
      setShowPublishForm(false);
    } catch (error) {
      console.error("Error publishing movie:", error);
      setSnackbarMessage("Failed to publish movie. Please try again.");
      setSnackbarSeverity("error");
    } finally {
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Just now";
    if (timestamp.toDate) return timestamp.toDate().toLocaleString();
    if (timestamp instanceof Date) return timestamp.toLocaleString();
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <StyledContainer>
        <CircularProgress sx={{ color: "#7d1aed" }} />
      </StyledContainer>
    );
  }

  const userHandle = userData?.handle || `@${userData?.email.split("@")[0]}`;
  const userName = userData?.name || userData?.email.split("@")[0];

  return (
    <StyledContainer>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          my: 10,
          width: "100%",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {/* Second Box: Personal/Producer Details */}
        <StyledBox>
          {userData?.isProducer === 1 ? (
            <>
              <Typography variant="h5" sx={{ color: "#ffffff", mb: 3, textAlign: "center" }}>
                Producer Profile
              </Typography>
              <Box sx={{ lineHeight: 2, color: "#cccccc" }}>
                <Typography><strong>Email:</strong> {userData?.email}</Typography>
                <Typography><strong>Account Type:</strong> Producer</Typography>
                <Typography><strong>Created At:</strong> {userData?.createdAt.toDate().toLocaleDateString()}</Typography>
              </Box>
              {!showPublishForm && (
                <Button
                  variant="contained"
                  sx={{ backgroundColor: "#7d1aed", color: "#ffffff", mt: 3, py: 1.5, borderRadius: 1, "&:hover": { backgroundColor: "#7d1aed" } }}
                  fullWidth
                  onClick={() => setShowPublishForm(true)}
                >
                  Publish Movie
                </Button>
              )}
              {showPublishForm && (
                <PublishMovieForm onSubmit={handlePublishMovie} onCancel={() => setShowPublishForm(false)} />
              )}
            </>
          ) : (
            <>
              <Typography variant="h5" sx={{ color: "#ffffff", mb: 3, textAlign: "center" }}>
                Personal Details
              </Typography>
              <Box sx={{ lineHeight: 2, color: "#cccccc" }}>
                <Typography><strong>Email:</strong> {userData?.email}</Typography>
                <Typography><strong>Account Type:</strong> Regular User</Typography>
                <Typography><strong>Created At:</strong> {userData?.createdAt.toDate().toLocaleDateString()}</Typography>
              </Box>
            </>
          )}
        </StyledBox>

        {/* First Box: Styled Profile Page with User's Posts */}
        <StyledBox sx={{ maxWidth: 600 }}>
          <Box className="profile-container" sx={{ backgroundColor: '#000', color: '#ccc', padding: 2 }}>
            <Box className="profile-header" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box className="username-container" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h1" className="username" sx={{ color: '#ccc', fontSize: '24px', fontWeight: 'bold' }}>
                  {userName}
                </Typography>
              </Box>
              <Box className="stats" sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 1 }}>
                <Box className="stat-item" sx={{ textAlign: 'center' }}>
                  <Typography className="stat-number" sx={{ color: '#ccc', fontSize: '24px', fontWeight: 'bold' }}>{posts.length}</Typography>
                  <Typography className="stat-label" sx={{ color: '#a0a0a0', fontSize: '14px' }}>Posts</Typography>
                </Box>
                <Box className="stat-item" sx={{ textAlign: 'center' }}>
                  <Typography className="stat-number" sx={{ color: '#ccc', fontSize: '24px', fontWeight: 'bold' }}>0</Typography>
                  <Typography className="stat-label" sx={{ color: '#a0a0a0', fontSize: '14px' }}>Followers</Typography>
                </Box>
                <Box className="stat-item" sx={{ textAlign: 'center' }}>
                  <Typography className="stat-number" sx={{ color: '#ccc', fontSize: '24px', fontWeight: 'bold' }}>0</Typography>
                  <Typography className="stat-label" sx={{ color: '#a0a0a0', fontSize: '14px' }}>Following</Typography>
                </Box>
              </Box>
            </Box>

            {/* Display User's Posts from SocialPage */}
            <Box sx={{ marginTop: 2 }}>
              <Typography variant="h2" sx={{ color: '#ccc', fontSize: '20px', fontWeight: 'bold', mb: 2 }}>
                Your Social Posts
              </Typography>
              {posts.length === 0 ? (
                <Typography sx={{ color: '#a0a0a0' }}>No posts yet</Typography>
              ) : (
                posts.map((post) => (
                  <PostCard key={post.id}>
                    <CardContent sx={{ padding: 2 }}>
                      <Typography variant="caption" color="textSecondary" gutterBottom>
                        {formatTimestamp(post.timestamp)}
                      </Typography>
                      <Typography variant="body1" gutterBottom sx={{ color: '#fff' }}>
                        {post.content || "No content"}
                      </Typography>
                      {post.image && (
                        <Box sx={{ position: 'relative', width: '100%', height: '300px' }}>
                          <CardMedia
                            component="img"
                            height="300"
                            image={post.image}
                            alt="Post Image"
                            sx={{ borderRadius: "10px", mb: 2, objectFit: 'cover' }}
                            onError={(e) => {
                              console.error("Error loading image in Profile:", post.image);
                              e.target.style.display = 'none';
                            }}
                            onLoad={() => console.log("Image loaded successfully in Profile:", post.image)}
                          />
                          {post.image && !post.image.startsWith('http') && (
                            <Typography sx={{ color: 'red', position: 'absolute', top: 0, left: 0 }}>
                              Invalid image URL
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </PostCard>
                ))
              )}
            </Box>
          </Box>
        </StyledBox>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%", backgroundColor: "#2d2d2d", color: "#ffffff" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
};

export default Profile;